import { useState, useEffect } from "react";
import API from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, User, Building2, Phone, Globe, ShieldCheck, MapPin, X, Plus } from "lucide-react";
import { toast } from "sonner";

const AREA_TYPE_LABELS = { zip_code: "ZIP Code", city: "City", county: "County", radius: "Radius" };

export default function VendorProfile() {
  const [vendor, setVendor] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [newAreaType, setNewAreaType] = useState("zip_code");
  const [newAreaFields, setNewAreaFields] = useState({});

  useEffect(() => {
    API.get("/technicians/me").then(({ data }) => {
      setVendor(data);
      setForm(data);
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await API.put("/technicians/me", {
        first_name: form.first_name, last_name: form.last_name,
        company_name: form.company_name, business_bio: form.business_bio,
        phone_number: form.phone_number, alternate_phone_number: form.alternate_phone_number,
        email_public: form.email_public, website_url: form.website_url,
        years_in_business: form.years_in_business ? parseInt(form.years_in_business) : null,
        license_number: form.license_number, license_state: form.license_state,
        insurance_provider: form.insurance_provider,
        insurance_policy_number: form.insurance_policy_number,
        service_radius_miles: form.service_radius_miles ? parseInt(form.service_radius_miles) : null,
        emergency_available: form.emergency_available,
      });
      setVendor(data);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const addServiceArea = async () => {
    const payload = { area_type: newAreaType, ...newAreaFields };
    if (newAreaType === "zip_code" && !payload.zip_code?.trim()) { toast.error("Enter ZIP code"); return; }
    if (newAreaType === "city" && !payload.city?.trim()) { toast.error("Enter city name"); return; }
    if (newAreaType === "county" && !payload.county?.trim()) { toast.error("Enter county name"); return; }
    if (newAreaType === "radius" && !payload.center_zip?.trim()) { toast.error("Enter center ZIP"); return; }

    try {
      await API.post("/technicians/me/service-areas", payload);
      setNewAreaFields({});
      const { data } = await API.get("/technicians/me");
      setVendor(data);
      setForm(data);
      toast.success("Coverage area added");
    } catch (err) {
      toast.error("Failed to add");
    }
  };

  const removeServiceArea = async (areaId) => {
    try {
      await API.delete(`/technicians/me/service-areas/${areaId}`);
      const { data } = await API.get("/technicians/me");
      setVendor(data);
      setForm(data);
      toast.success("Removed");
    } catch (err) {
      toast.error("Failed to remove");
    }
  };

  const getAreaLabel = (area) => {
    switch (area.area_type) {
      case "city": return `${area.city || ""}${area.state ? `, ${area.state}` : ""} (City)`;
      case "county": return `${area.county || ""}${area.state ? `, ${area.state}` : ""} (County)`;
      case "radius": return `${area.radius_miles || 25}mi from ${area.center_zip || ""} (Radius)`;
      default: return area.zip_code || "ZIP";
    }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  if (!vendor) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-6 h-6 border-2 border-zinc-900 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="vendor-profile-page">
      <h2 className="text-xl font-heading font-bold text-zinc-900">Profile</h2>

      {/* Personal Info */}
      <Card className="border border-zinc-200">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <User className="w-4 h-4 text-zinc-400" />
            <h3 className="text-sm font-heading font-semibold text-zinc-900">Personal Info</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-zinc-500">First Name</Label>
              <Input value={form.first_name || ""} onChange={e => update("first_name", e.target.value)} data-testid="input-first-name" className="mt-1" /></div>
            <div><Label className="text-xs text-zinc-500">Last Name</Label>
              <Input value={form.last_name || ""} onChange={e => update("last_name", e.target.value)} data-testid="input-last-name" className="mt-1" /></div>
          </div>
          <div><Label className="text-xs text-zinc-500">Phone</Label>
            <Input value={form.phone_number || ""} onChange={e => update("phone_number", e.target.value)} data-testid="input-phone" className="mt-1" /></div>
          <div><Label className="text-xs text-zinc-500">Public Email</Label>
            <Input value={form.email_public || ""} onChange={e => update("email_public", e.target.value)} data-testid="input-email-public" className="mt-1" /></div>
        </CardContent>
      </Card>

      {/* Business Info */}
      <Card className="border border-zinc-200">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Building2 className="w-4 h-4 text-zinc-400" />
            <h3 className="text-sm font-heading font-semibold text-zinc-900">Business</h3>
          </div>
          <div><Label className="text-xs text-zinc-500">Company Name</Label>
            <Input value={form.company_name || ""} onChange={e => update("company_name", e.target.value)} data-testid="input-company" className="mt-1" /></div>
          <div><Label className="text-xs text-zinc-500">Bio</Label>
            <Textarea value={form.business_bio || ""} onChange={e => update("business_bio", e.target.value)} rows={3} data-testid="input-bio" className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-zinc-500">Years in Business</Label>
              <Input type="number" value={form.years_in_business || ""} onChange={e => update("years_in_business", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs text-zinc-500">Service Radius (mi)</Label>
              <Input type="number" value={form.service_radius_miles || ""} onChange={e => update("service_radius_miles", e.target.value)} className="mt-1" /></div>
          </div>
          <div><Label className="text-xs text-zinc-500">Website</Label>
            <Input value={form.website_url || ""} onChange={e => update("website_url", e.target.value)} className="mt-1" /></div>
          <div className="flex items-center justify-between py-2">
            <Label className="text-sm text-zinc-600">Emergency Available</Label>
            <Switch checked={form.emergency_available || false} onCheckedChange={v => update("emergency_available", v)} data-testid="emergency-toggle" />
          </div>
        </CardContent>
      </Card>

      {/* Licensing */}
      <Card className="border border-zinc-200">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-4 h-4 text-zinc-400" />
            <h3 className="text-sm font-heading font-semibold text-zinc-900">Licensing & Insurance</h3>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-zinc-500">License #</Label>
              <Input value={form.license_number || ""} onChange={e => update("license_number", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs text-zinc-500">License State</Label>
              <Input value={form.license_state || ""} onChange={e => update("license_state", e.target.value)} className="mt-1" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-zinc-500">Insurance Provider</Label>
              <Input value={form.insurance_provider || ""} onChange={e => update("insurance_provider", e.target.value)} className="mt-1" /></div>
            <div><Label className="text-xs text-zinc-500">Policy #</Label>
              <Input value={form.insurance_policy_number || ""} onChange={e => update("insurance_policy_number", e.target.value)} className="mt-1" /></div>
          </div>
        </CardContent>
      </Card>

      {/* Coverage Areas */}
      <Card className="border border-zinc-200">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 mb-1">
            <MapPin className="w-4 h-4 text-zinc-400" />
            <h3 className="text-sm font-heading font-semibold text-zinc-900">Coverage Areas</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {(vendor.service_areas || []).map(area => (
              <Badge key={area.id} variant="secondary" className="flex items-center gap-1 pr-1">
                {getAreaLabel(area)}
                <button onClick={() => removeServiceArea(area.id)} className="ml-1 rounded-full hover:bg-zinc-300 p-0.5">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
            {(vendor.service_areas || []).length === 0 && (
              <p className="text-xs text-zinc-400">No coverage areas added yet</p>
            )}
          </div>

          <div className="space-y-2 pt-2 border-t border-zinc-100">
            <Label className="text-xs text-zinc-500">Add Coverage Area</Label>
            <Select value={newAreaType} onValueChange={v => { setNewAreaType(v); setNewAreaFields({}); }}>
              <SelectTrigger className="w-full" data-testid="area-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="zip_code">ZIP Code</SelectItem>
                <SelectItem value="city">City</SelectItem>
                <SelectItem value="county">County</SelectItem>
                <SelectItem value="radius">Radius</SelectItem>
              </SelectContent>
            </Select>

            {newAreaType === "zip_code" && (
              <div className="flex gap-2">
                <Input placeholder="ZIP Code (e.g. 75001)" value={newAreaFields.zip_code || ""}
                  onChange={e => setNewAreaFields(p => ({ ...p, zip_code: e.target.value }))}
                  className="flex-1" data-testid="input-new-zip"
                  onKeyDown={e => e.key === "Enter" && addServiceArea()} />
                <Button size="sm" variant="outline" onClick={addServiceArea} data-testid="add-area-button">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            )}

            {newAreaType === "city" && (
              <div className="flex gap-2">
                <Input placeholder="City" value={newAreaFields.city || ""}
                  onChange={e => setNewAreaFields(p => ({ ...p, city: e.target.value }))}
                  className="flex-1" data-testid="input-new-city" />
                <Input placeholder="State" value={newAreaFields.state || ""}
                  onChange={e => setNewAreaFields(p => ({ ...p, state: e.target.value }))}
                  className="w-20" data-testid="input-new-state" />
                <Button size="sm" variant="outline" onClick={addServiceArea} data-testid="add-area-button">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            )}

            {newAreaType === "county" && (
              <div className="flex gap-2">
                <Input placeholder="County name" value={newAreaFields.county || ""}
                  onChange={e => setNewAreaFields(p => ({ ...p, county: e.target.value }))}
                  className="flex-1" data-testid="input-new-county" />
                <Input placeholder="State" value={newAreaFields.state || ""}
                  onChange={e => setNewAreaFields(p => ({ ...p, state: e.target.value }))}
                  className="w-20" data-testid="input-new-county-state" />
                <Button size="sm" variant="outline" onClick={addServiceArea} data-testid="add-area-button">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            )}

            {newAreaType === "radius" && (
              <div className="flex gap-2">
                <Input placeholder="Center ZIP" value={newAreaFields.center_zip || ""}
                  onChange={e => setNewAreaFields(p => ({ ...p, center_zip: e.target.value }))}
                  className="flex-1" data-testid="input-center-zip" />
                <Input placeholder="Miles" value={newAreaFields.radius_miles || ""}
                  onChange={e => setNewAreaFields(p => ({ ...p, radius_miles: e.target.value }))}
                  className="w-20" type="number" data-testid="input-radius-miles" />
                <Button size="sm" variant="outline" onClick={addServiceArea} data-testid="add-area-button">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="w-full h-11 bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="save-profile-button">
        {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Save Profile</>}
      </Button>
    </div>
  );
}
